import { type FC, memo, type RefObject, useEffect, useMemo } from 'react'
import { Box, Grid, GridItem, type GridProps, HStack, VStack } from '@chakra-ui/react'
import type { AssistantDTO } from 'ecosystem'
import { BiCollapseAlt, BiExpandAlt } from 'react-icons/bi'
import GrandIconButton from 'ui/common/GrandIconButton'
import { useUser } from 'auth'
import { CgClose } from 'react-icons/cg'
import { useOverridesContext } from 'ui/lib/overrides/hooks'
import { overrideText } from 'ui/lib/overrides'
import Chat from '../Chat'
import {
  type AssistantContextState,
  type AssistantTextOverrides,
  type ConversationalMode,
  ConversationalModeStatus
} from '../../types'
import { AssistantProvider } from '../asssistantContext'
import ChatProducts from '../ChatProducts'
import { Avatar } from '../Avatar'
import { useSetReset } from '../../../common/hooks'
import { BG_GRADIENT } from '../constants'
import { useAssistantV2 } from './useAssistantV2'
import { getMessagesElement, handleScrollBottom, waitForElement } from './utils'

interface MainProps extends GridProps {
  onClose: () => void
  paths: {
    chat: string
    settings: string
    thread: string
    products: string
    textToSpeech: string
  }
  settings: AssistantDTO
  inputRef: RefObject<HTMLInputElement>
  components?: AssistantContextState['components']
}

const Main: FC<MainProps> = ({ inputRef, onClose, paths, settings, components, ...props }) => {
  const overrides = useOverridesContext<keyof AssistantTextOverrides>()

  const {
    inputValue,
    setInputValue,
    isSendingMessage,
    sendMessage,
    history,
    newMessages,
    sendInitRequest,
    initError,
    errors,
    isInit,
    isExpanded,
    toggleSize,
    textToSpeech,
    isLoadingTextToSpeech,
    isPlayingTextToSpeech,
    onSpeakAudioRef,
    textToSpeechError
  } = useAssistantV2({
    paths
  })

  const { user } = useUser()

  useEffect(() => {
    handleScrollBottom()
  }, [isInit])

  useEffect(() => {
    if (!newMessages?.length) {
      return
    }

    waitForElement('#ai-widget__loader', (loaderElement) => {
      if (loaderElement) {
        loaderElement.scrollIntoView({ behavior: 'smooth' })
      }
    })

    const { id, role } = newMessages[newMessages.length - 1]

    if (role === 'client') {
      handleScrollBottom()
      return
    }

    const messageElement = getMessagesElement()?.querySelector(`[data-message-id="${id}"]`)

    if (messageElement) {
      messageElement.scrollIntoView({ behavior: 'smooth' })
    }
  }, [newMessages])

  const [conversationalMode, dispatchConversationalMode] = useSetReset<ConversationalMode>({
    status: ConversationalModeStatus.NotInitialized
  })

  const assistantProviderValue: AssistantContextState = useMemo(() => {
    return {
      onSpeakAudioRef,
      conversationalMode,
      dispatchConversationalMode,
      onClose,
      inputValue,
      setInputValue,
      isLoading: isSendingMessage,
      handleSend: sendMessage,
      history,
      inputRef,
      initError,
      reinit: sendInitRequest,
      errors,
      isInit,
      settings,
      components,
      paths
    }
  }, [
    onSpeakAudioRef,
    conversationalMode,
    dispatchConversationalMode,
    onClose,
    inputValue,
    setInputValue,
    isSendingMessage,
    sendMessage,
    history,
    inputRef,
    initError,
    sendInitRequest,
    errors,
    isInit,
    settings,
    components,
    paths
  ])

  return (
    <AssistantProvider value={assistantProviderValue}>
      <Grid
        gap="0"
        gridTemplateRows="55px calc(100% - 55px)"
        h={{
          base: `calc(${isExpanded ? '90' : '60'}svh - 90px)`,
          sm: `calc(${isExpanded ? '80' : '60'}svh - 90px)`
        }}
        p={0}
        templateColumns="repeat(1, 1fr)"
        w="full"
        {...props}>
        <GridItem borderBottomWidth="1px" borderBottomColor="gray.50" className="assistant-header">
          <HStack
            borderTopRadius="md"
            h="100%"
            justifyContent="space-between"
            px={4}
            w="100%"
            color="text.default"
            py={2}>
            <HStack w="full">
              <VStack position="relative">
                <Box
                  borderRadius="50%"
                  bg={BG_GRADIENT}
                  p="2px"
                  position="relative"
                  display="flex"
                  alignItems="center"
                  justifyContent="center">
                  <Avatar
                    borderRadius="50%"
                    h="35px"
                    w="35px"
                    imageProps={{ src: settings?.imgUrl, sizes: '100px' }}
                  />
                </Box>

                <Box
                  position="absolute"
                  bottom="0"
                  right="0"
                  w="8px"
                  h="8px"
                  backgroundColor="#00ff00"
                  borderRadius="50%"
                />
              </VStack>

              <VStack alignItems="flex-start" gap={0}>
                <Box fontSize="md" fontWeight="medium" color="gray.700">
                  {settings?.name ?? 'AI assistant'}
                </Box>

                {!!user?.customer && (
                  <Box fontSize="sm">
                    {`
                    ${overrideText('Chatta med', overrides?.assistantChatWith)} 
                    ${user?.customer.firstName ?? overrideText('Kund', overrides?.assistantChatWithFallback)}
                    `}
                  </Box>
                )}
              </VStack>
            </HStack>

            <HStack spacing={0} px={2} position="absolute" right={0}>
              <GrandIconButton
                aria-label={
                  isExpanded
                    ? overrideText('Krympa', overrides?.assistantShrink)
                    : overrideText('Expandera', overrides?.assistantShrink)
                }
                fontSize="xl"
                icon={isExpanded ? <BiCollapseAlt /> : <BiExpandAlt />}
                minW={undefined}
                onClick={toggleSize}
                shadow="none"
                tooltip={
                  isExpanded
                    ? overrideText('Krympa', overrides?.assistantShrink)
                    : overrideText('Expandera', overrides?.assistantShrink)
                }
                variant="ghost"
              />

              <GrandIconButton
                aria-label={overrideText('Minimera', overrides?.assistantMinimize)}
                fontSize="lg"
                icon={<CgClose />}
                minW={undefined}
                onClick={onClose}
                shadow="none"
                tooltip={overrideText('Minimera', overrides?.assistantMinimize)}
                variant="ghost"
              />
            </HStack>
          </HStack>
        </GridItem>

        <GridItem>
          <Chat
            isConversationalModeEnabled
            isPlayingSpeech={isPlayingTextToSpeech}
            isLoadingSpeech={isLoadingTextToSpeech}
            onSpeak={textToSpeech}
            onSpeakError={textToSpeechError}
            isExpanded={isExpanded}
            renderProductsComponent={(products) => <ChatProducts products={products} />}
          />
        </GridItem>
      </Grid>
    </AssistantProvider>
  )
}

export default memo(Main)
